import React from 'react';
import { Box, Button, Container, Typography, Paper } from '@mui/material';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const GradientButton = styled(Button)(({ theme, variant }) => ({
  background: variant === 'contained' 
    ? 'linear-gradient(to right, #6a0dad, #5a0b8a)' 
    : 'transparent',
  color: variant === 'contained' ? 'white' : '#6a0dad',
  border: variant === 'outlined' ? '2px solid #6a0dad' : 'none',
  fontWeight: 'bold',
  padding: '12px 28px',
  borderRadius: '8px',
  boxShadow: variant === 'contained' ? '0 4px 10px rgba(106, 13, 173, 0.3)' : 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: variant === 'contained' 
      ? 'linear-gradient(to right, #5a0b8a, #4a0a7a)' 
      : 'rgba(106, 13, 173, 0.05)',
    boxShadow: variant === 'contained' ? '0 6px 15px rgba(106, 13, 173, 0.4)' : 'none',
    transform: 'translateY(-2px)',
    border: variant === 'outlined' ? '2px solid #5a0b8a' : 'none',
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(6),
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(138, 75, 175, 0.1)',
  overflow: 'hidden',
  backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : 'white',
}));

const SuccessIcon = styled(Box)(({ theme }) => ({
  width: 90,
  height: 90,
  borderRadius: '50%',
  background: 'linear-gradient(to right, #6a0dad, #5a0b8a)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  color: 'white',
  fontSize: '44px',
  fontWeight: 'bold',
  boxShadow: '0 8px 20px rgba(106, 13, 173, 0.3)',
}));

const DonationSuccessPage = () => {
  const { t } = useTranslation();
  
  return (
    <Box className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 min-h-screen py-12">
      <Container maxWidth="md">
        <StyledPaper 
          elevation={0} 
        >
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0.03,
              backgroundImage: `url(/images/bubble.png)`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              pointerEvents: 'none',
              zIndex: 0
            }} 
          />
          
          <Box sx={{ position: 'relative', zIndex: 1, width: '100%', textAlign: 'center' }}>
            <SuccessIcon>
              ✓
            </SuccessIcon>
            
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom 
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(to right, #6a0dad, #5a0b8a)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              {t('donationSuccessThankYou')}
            </Typography>
            
            <Box 
              className="w-20 h-1 mx-auto mb-6" 
              sx={{ 
                background: 'linear-gradient(to right, #6a0dad, #5a0b8a)',
                borderRadius: '999px'
              }} 
            />
            
            <Typography 
              variant="body1" 
              color="text.secondary" 
              paragraph 
              sx={{ 
                mb: 6, 
                maxWidth: '600px', 
                mx: 'auto',
                lineHeight: 1.7,
                fontSize: '1.05rem'
              }}
            >
              {t('donationSuccessMessage')}
            </Typography>
            
            <Box sx={{ mt: 2, display: 'flex', gap: 3, justifyContent: 'center' }}>
              <GradientButton 
                variant="contained" 
                component={Link} 
                to="/"
                size="large"
              >
                {t('backToHome')}
              </GradientButton>
              
              <GradientButton 
                variant="outlined"
                component={Link} 
                to="/donation"
                size="large"
              >
                {t('donateAgain')}
              </GradientButton>
            </Box>
          </Box>
        </StyledPaper>
      </Container>
    </Box>
  );
};

export default DonationSuccessPage; 